import { ELEMENTS, ELEMENT_REGISTRY } from "./ElementRegistry.ts";
import { COLORS } from "../core/constants.ts";
import { RARITY_SLOT_COLORS } from "../core/palette.ts";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
  shape: string;
  char: string;
  gravity: number;
  active: boolean;
}

interface ParticleConfig {
  colors: string[];
  shape: string;
}

/** Synergy/fusion FX particle profiles (not elements). */
const FUSION_PARTICLES: Record<string, ParticleConfig> = {
  steam: { colors: ["#88ddff", "#aaeeff", "#ffffff"], shape: "binary" },
  plasma: { colors: ["#ff44aa", "#ff66cc", "#ff88dd"], shape: "binary" },
  nexus: {
    colors: [COLORS.SENTINEL_GREEN, "#44ffaa", "#88ffcc"],
    shape: "binary",
  },
  // Payload detonation profiles (PT-14): gold board-nuke / violet implosion.
  payload_gold: {
    colors: ["#FFD700", "#FFE066", "#FFF4B0", "#ffffff"],
    shape: "binary",
  },
  payload_void: { colors: [RARITY_SLOT_COLORS.epic!, "#C77DFF", "#E0AAFF"], shape: "binary" },
  // BL-70: per-payload particle profiles.
  payload_green: { colors: [COLORS.SENTINEL_GREEN, "#7FFF7F", "#CCFFCC"], shape: "binary" },
  payload_amber: { colors: [COLORS.AMBER_WARN, "#FFE066", "#FFF4B0"], shape: "binary" },
  payload_cyan: { colors: [COLORS.ICE_CYAN, "#80EAFF", "#CCF5FF"], shape: "binary" },
  payload_virus: { colors: ["#00FF66", "#66FFB2", "#99FFCC"], shape: "binary" },
  payload_red: { colors: [COLORS.ORACLE_RED, "#FF6688", "#FFAACC"], shape: "binary" },
};

export const ELEMENT_PARTICLES: Record<string, ParticleConfig> = {
  ...Object.fromEntries(
    ELEMENTS.map((el) => [el, ELEMENT_REGISTRY[el].particles]),
  ),
  ...FUSION_PARTICLES,
};

const BINARY_CHARS = ["0", "1", "0", "1", "0", "1", "{", "}", "<", ">"];
const MAX_PARTICLES = 500;

export class ParticleSystem {
  private pool: Particle[] = [];
  private activeList: Particle[] = [];
  private fontCache = new Map<number, string>();
  private ctx: CanvasRenderingContext2D;
  /** BL-44 ladder rung 2 — adaptive live-particle cap. The 500-slot pool is
   *  untouched (no realloc/thrash); under load the PerfGovernor lowers this
   *  ceiling so over-emission is dropped at {@link acquire}. Defaults to the
   *  full pool, so without a governor every frame is byte-identical. */
  private softCap = MAX_PARTICLES;

  constructor(ctx: CanvasRenderingContext2D) {
    this.ctx = ctx;
    for (let i = 0; i < MAX_PARTICLES; i++) {
      this.pool.push(this.createParticle());
    }
  }

  get activeCount(): number {
    return this.activeList.length;
  }

  /** Set the adaptive live-particle ceiling (BL-44). Clamped to [0, pool size];
   *  emits beyond it are dropped (the pool itself never shrinks). Render-only —
   *  particles never feed the sim, so this is determinism-neutral. */
  setSoftCap(cap: number): void {
    this.softCap = cap < 0 ? 0 : cap > MAX_PARTICLES ? MAX_PARTICLES : cap;
  }

  /** The current adaptive ceiling (dev-overlay read). */
  get softCapValue(): number {
    return this.softCap;
  }

  emit(x: number, y: number, element: string, count = 8): void {
    const config = ELEMENT_PARTICLES[element] ?? ELEMENT_PARTICLES["fire"]!;

    for (let i = 0; i < count; i++) {
      const p = this.acquire();
      if (!p) break;

      const angle = Math.random() * Math.PI * 2;
      const speed = 40 + Math.random() * 80;
      const color =
        config.colors[Math.floor(Math.random() * config.colors.length)]!;

      p.x = x;
      p.y = y;
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed;
      p.life = 0.4 + Math.random() * 0.4;
      p.maxLife = p.life;
      p.size = 2 + Math.random() * 3;
      p.color = color;
      p.shape = config.shape;
      p.char = BINARY_CHARS[Math.floor(Math.random() * BINARY_CHARS.length)]!;
      p.gravity = 30;
      p.active = true;
    }
  }

  emitKill(x: number, y: number, element: string): void {
    this.emit(x, y, element, 16);
  }

  /** Ring burst: particles seeded on a circle of `radius`, drifting outward —
   *  telegraphs an area-of-effect impact (e.g. the virus infection zone) so
   *  the player can read where and how large the affected area is. */
  emitRing(
    x: number,
    y: number,
    radius: number,
    element: string,
    count = 16,
  ): void {
    const config = ELEMENT_PARTICLES[element] ?? ELEMENT_PARTICLES["fire"]!;

    for (let i = 0; i < count; i++) {
      const p = this.acquire();
      if (!p) break;

      const angle = (i / count) * Math.PI * 2;
      const speed = 20 + Math.random() * 35;
      const color =
        config.colors[Math.floor(Math.random() * config.colors.length)]!;

      p.x = x + Math.cos(angle) * radius;
      p.y = y + Math.sin(angle) * radius;
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed;
      p.life = 0.5 + Math.random() * 0.3;
      p.maxLife = p.life;
      p.size = 2 + Math.random() * 2;
      p.color = color;
      p.shape = config.shape;
      p.char = BINARY_CHARS[Math.floor(Math.random() * BINARY_CHARS.length)]!;
      p.gravity = 0;
      p.active = true;
    }
  }

  emitSynergy(x: number, y: number, synergyId: string): void {
    const element =
      synergyId === "steam"
        ? "steam"
        : synergyId === "plasma"
          ? "plasma"
          : "nexus";
    this.emit(x, y, element, 12);
  }

  emitFlash(x: number, y: number): void {
    const p = this.acquire();
    if (!p) return;
    p.x = x;
    p.y = y;
    p.vx = 0;
    p.vy = 0;
    p.life = 0.08;
    p.maxLife = 0.08;
    p.size = 12;
    p.color = "#FFFFFF";
    p.shape = "circle";
    p.char = "";
    p.gravity = 0;
    p.active = true;
  }

  update(dt: number): void {
    const dtSec = dt / 1000;

    for (let i = this.activeList.length - 1; i >= 0; i--) {
      const p = this.activeList[i]!;
      p.life -= dtSec;

      if (p.life <= 0) {
        p.active = false;
        // Swap-remove O(1) instead of splice O(n)
        const last = this.activeList[this.activeList.length - 1]!;
        this.activeList[i] = last;
        this.activeList.pop();
        this.pool.push(p);
        continue;
      }

      p.x += p.vx * dtSec;
      p.y += p.vy * dtSec;
      p.vy += p.gravity * dtSec;
      p.vx *= 0.98;
    }
  }

  render(): void {
    const ctx = this.ctx;
    ctx.font = "bold 10px 'Courier New', monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    for (const p of this.activeList) {
      const alpha = p.life / p.maxLife;
      ctx.globalAlpha = alpha;
      ctx.fillStyle = p.color;

      if (p.shape === "binary") {
        const fontSize = (8 + p.size) | 0; // quantize to integer
        ctx.font = this.getFontString(fontSize);
        ctx.fillText(p.char, p.x, p.y);
      } else {
        const size = p.size * alpha;
        ctx.beginPath();
        ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.globalAlpha = 1;
    ctx.shadowBlur = 0;
    ctx.textAlign = "start";
  }

  private acquire(): Particle | null {
    // BL-44: honour the adaptive ceiling before the hard pool limit, so under
    // load over-emission is shed gracefully (the pool stays full-size).
    if (this.activeList.length >= this.softCap) return null;
    if (this.pool.length === 0) return null;
    const p = this.pool.pop()!;
    this.activeList.push(p);
    return p;
  }

  private createParticle(): Particle {
    return {
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      life: 0,
      maxLife: 0,
      size: 0,
      color: "#fff",
      shape: "binary",
      char: "1",
      gravity: 0,
      active: false,
    };
  }

  private getFontString(size: number): string {
    let s = this.fontCache.get(size);
    if (!s) {
      s = `bold ${size}px 'Courier New', monospace`;
      this.fontCache.set(size, s);
    }
    return s;
  }
}
